import { UseQueryStateBaseResult } from 'store/api/api.types';
import { EmptyStateItemType, RecentSearchItem, SuggestionItem, SuggestionResult } from 'core/type/SuggestionResult.types';
import { BootstrapConfig } from 'core/type/BootstrapConfig.types';
import { measureTextSearchApiResponse } from 'store/api/measurements/homePageMeasurements';
import { isQueryLoading } from 'store/api/apiUtils';
import mockSuggestionList from 'mock/textSearch/mock-suggestion-list';
import { getItemFromLocalStorage, setItemInLocalStorage } from 'core-framework/window/storage';
import { LocalStorageKey } from 'core-framework/constant/StorageKeys';
import { TextSearchType } from 'core/type/TextSearch.types';
import { QueryString } from 'core/utils/queryString/queryString';
import { SearchQueryKey } from 'core-framework/constant/SearchQueryKey';

export const MAX_RECENT_SEARCH = 5;

export type SuggestionSearchResponse = UseQueryStateBaseResult<{ suggestionList: SuggestionResult[] }>;

export const getSuggestions = (response: SuggestionSearchResponse, searchText: string, locale: string, pageTypeId: number, config: BootstrapConfig): SuggestionResult[] => {
    if (config.isMockData) {
        if (searchText) {
            const mockQuery = searchText.slice(0, 4); // Select first 4 characters to simulate proximate match
            const results = mockSuggestionList.filter((s) => s.name.toLocaleLowerCase().includes(mockQuery));
            return results;
        }
        return mockSuggestionList;
    }
    if (!isQueryLoading(response) && !!searchText) {
        measureTextSearchApiResponse(response, searchText, pageTypeId, config);
    }
    if (response.isSuccess) {
        return (response.data && response.data.suggestionList) || [];
    }
    return [];
};

export const setRecentlySearch = (recentItem: RecentSearchItem, maxLength: number, emptyStateItemType?: EmptyStateItemType) => {
    if (!recentItem.id || !recentItem.type || !recentItem.url) return;
    const localStorageKey = recentItem.type === TextSearchType.City ? LocalStorageKey.RECENT_CITIES : LocalStorageKey.RECENT_ACTIVITIES;
    const localStorageItems = getItemFromLocalStorage(localStorageKey);
    let recentSearchesInStorage: RecentSearchItem[] = localStorageItems ? (JSON.parse(localStorageItems) as RecentSearchItem[]) : [];
    recentSearchesInStorage = recentSearchesInStorage.filter((item) => item.id !== recentItem.id);

    const [pathname, queryParams = ''] = recentItem.url.split('?');
    const sanitizedQueryParams = new QueryString(queryParams);
    // Remove specific query params, so that when user redirected, settings are not overridden
    sanitizedQueryParams.delete(SearchQueryKey.Keyword, SearchQueryKey.Currency);
    recentItem.url = `${pathname}?${sanitizedQueryParams.toString()}`;

    recentSearchesInStorage.unshift(recentItem);

    if (recentSearchesInStorage.length > maxLength) {
        recentSearchesInStorage.pop();
    }

    setItemInLocalStorage(localStorageKey, JSON.stringify(recentSearchesInStorage));
};

export const getRecentlySearch = (storageKey: LocalStorageKey): RecentSearchItem[] => {
    const localStorageItems = getItemFromLocalStorage(storageKey);
    return localStorageItems ? (JSON.parse(localStorageItems) as RecentSearchItem[]) : [];
};

export const transformSuggestionToRecentSearch = (item: SuggestionResult | SuggestionItem, emptyStateType?: EmptyStateItemType): RecentSearchItem => {
    return {
        id: item.id,
        type: item.type,
        activityName: item.activityName || item.activityNameTranslated,
        cityName: item.cityName || item.cityNameTranslated,
        countryName: item.countryName || item.countryNameTranslated,
        cityId: item.cityId,
        url: item.url,
        isUrlWithPrefix: !!emptyStateType,
        isCityPageEligible: item.isCityPageEligible,
        supplierId: item.supplierId,
        emptyStateType,
    } as RecentSearchItem;
};
